import React from 'react';
import {
  Grid,
  Typography,
  Box,
  styled,
  MenuItem
} from '@mui/material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { BasePaper, GlassInput } from './styles/SharedStyles';
import { FiClock, FiCalendar } from 'react-icons/fi';

interface RecurringScheduleFormProps {
  schedule: {
    frequency: 'weekly' | 'monthly' | 'quarterly' | 'annually';
    startDate: Date;
    endDate: Date | null;
  };
  onScheduleChange: (schedule: any) => void;
  isScheduledPayment?: boolean;
}

const SchedulePaper = styled(BasePaper)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  background: 'rgba(26, 31, 46, 0.6)',
  backdropFilter: 'blur(12px)',
  width: '100%',
}));

const HeaderTypography = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.9)',
  fontWeight: 600,
  letterSpacing: '-0.02em',
  marginBottom: theme.spacing(3),
}));

const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
  width: '100%',
}));

const FrequencySelect = styled(GlassInput)(({ theme }) => ({
  '& .MuiSelect-select': {
    padding: theme.spacing(1.5),
    color: 'rgba(255, 255, 255, 0.9)',
  },
  '& .MuiOutlinedInput-root': {
    background: 'rgba(255, 255, 255, 0.03)',
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'all 0.2s ease',
    color: 'rgba(255, 255, 255, 0.9)',
    '&:hover': {
      border: '1px solid rgba(255, 255, 255, 0.2)',
      background: 'rgba(255, 255, 255, 0.05)',
    },
    '&.Mui-focused': {
      border: '1px solid #0EA5E9',
      boxShadow: '0 0 0 2px rgba(14, 165, 233, 0.2)',
    }
  },
  '& .MuiMenuItem-root': {
    padding: theme.spacing(1.5),
    color: 'rgba(255, 255, 255, 0.9)',
  }
}));

const InputLabel = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: '0.875rem',
  marginBottom: theme.spacing(1),
  fontWeight: 500,
}));

const InputWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  width: '100%',
}));

const StyledInput = styled(GlassInput)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    color: 'rgba(255, 255, 255, 0.9)',
    '& input': {
      color: 'rgba(255, 255, 255, 0.9)',
      '&::placeholder': {
        color: 'rgba(255, 255, 255, 0.5)',
        opacity: 1,
      }
    }
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0EA5E9',
  }
}));

const InfoText = styled(Box)(({ theme }) => ({
  color: '#22D3EE !important',
  fontSize: '0.875rem',
  marginTop: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '& svg': {
    fontSize: '1rem',
    color: '#0EA5E9 !important',
  }
}));

export const RecurringScheduleForm: React.FC<RecurringScheduleFormProps> = ({
  schedule,
  onScheduleChange,
  isScheduledPayment = false
}) => {
  return (
    <SchedulePaper>
      <HeaderTypography variant="h6">
        <FiClock />
        {isScheduledPayment ? 'Payment Schedule' : 'Recurring Schedule'}
      </HeaderTypography>

      <Grid container spacing={3}>
        {!isScheduledPayment && (
          <Grid item xs={12} md={4}>
            <InputWrapper>
              <InputLabel>Frequency</InputLabel>
              <StyledInput
                select
                fullWidth
                value={schedule.frequency}
                onChange={(e) => onScheduleChange({
                  ...schedule,
                  frequency: e.target.value
                })}
              >
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="annually">Annually</MenuItem>
              </StyledInput>
              <InfoText>
                <FiClock />
                How often should this payment recur?
              </InfoText>
            </InputWrapper>
          </Grid>
        )}

        <Grid item xs={12} md={4}>
          <InputWrapper>
            <InputLabel>
              {isScheduledPayment ? 'Payment Date' : 'Start Date'}
            </InputLabel>
            <DatePicker
              selected={schedule.startDate}
              onChange={(date: Date) => onScheduleChange({
                ...schedule,
                startDate: date
              })}
              customInput={
                <StyledInput
                  fullWidth
                  placeholder={isScheduledPayment ? "Select payment date" : "Select start date"}
                />
              }
              dateFormat="MMM d, yyyy"
              minDate={new Date()}
            />
            <InfoText>
              <FiCalendar />
              {isScheduledPayment 
                ? 'When should this payment be processed?' 
                : 'When should the recurring payments start?'}
            </InfoText>
          </InputWrapper>
        </Grid>

        {!isScheduledPayment && (
          <Grid item xs={12} md={4}>
            <InputWrapper>
              <InputLabel>End Date (Optional)</InputLabel>
              <DatePicker
                selected={schedule.endDate}
                onChange={(date: Date | null) => onScheduleChange({
                  ...schedule,
                  endDate: date
                })}
                customInput={
                  <StyledInput
                    fullWidth
                    placeholder="Select end date"
                  />
                }
                dateFormat="MMM d, yyyy"
                minDate={schedule.startDate || new Date()}
                isClearable
              />
              <InfoText>
                <FiCalendar />
                When should the recurring payments end?
              </InfoText>
            </InputWrapper>
          </Grid>
        )}
      </Grid>
    </SchedulePaper>
  );
};

export default RecurringScheduleForm; 